import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { BiQr } from 'react-icons/bi'; // Import QR Code icon from React Icons
import jsPDF from 'jspdf';
import QRCode from 'qrcode';
import { toast, ToastContainer } from 'react-toastify'; // Toast notifications
import 'react-toastify/dist/ReactToastify.css'; // Import styles for react-toastify
import OrgContext from '../context/OrgContext';

const EmployeesDialog = ({ location, onClose, handleUpdateLocation }) => {
    const [users, setUsers] = useState([]);
    const [selectedEmployees, setSelectedEmployees] = useState(location.employees || []);  // Pre-populate with current employees
    const [loading, setLoading] = useState(false);
    const token = sessionStorage.getItem('token');

    // Fetch all users for the account when the component loads
    useEffect(() => {
        const fetchUsers = async () => {
            try {
                setLoading(true);
                const { data: usersList } = await axios.get(`${process.env.REACT_APP_SRV}/users`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setUsers(usersList);
            } catch (error) {
                console.error('Error fetching users:', error);
                toast.error('Failed to load employees');
            } finally {
                setLoading(false);
            }
        };

        fetchUsers();
    }, [token]);

    // Toggle employee selection
    const handleEmployeeToggle = (userID) => {
        if (selectedEmployees.includes(userID)) {
            setSelectedEmployees(selectedEmployees.filter(id => id !== userID));
        } else {
            setSelectedEmployees([...selectedEmployees, userID]);
        }
    };

    // Save the updated employees (employees) list
    const handleSave = async () => {
        try {
            const updatedLocation = {
                ...location,
                employees: selectedEmployees,  // Update the employees field
            };
            await handleUpdateLocation(updatedLocation); // Call the parent update function
            toast.success('Employees updated successfully');
            onClose(); // Close the dialog
        } catch (error) {
            console.error('Error saving employees:', error);
            toast.error('Failed to save employees');
        }
    };

    return (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center">
            <div className="bg-gray-800 p-6 rounded-lg">
                <h3 className="text-xl mb-4">Assign Employees for {location.name}</h3>
                {loading ? (
                    <p>Loading...</p>
                ) : (
                    <ul>
                        {users.map(user => (
                            <li key={user._id} className="flex items-center">
                                <input
                                    type="checkbox"
                                    checked={selectedEmployees.includes(user._id)}  // Check if the user is already an employee
                                    onChange={() => handleEmployeeToggle(user._id)}
                                />
                                <span className="ml-2">{user.name}</span>
                            </li>
                        ))}
                    </ul>
                )}
                <div className="flex justify-end mt-4">
                    <button onClick={onClose} className="bg-red-600 px-4 py-2 rounded hover:bg-red-700">Cancel</button>
                    <button onClick={handleSave} className="bg-green-600 px-4 py-2 ml-2 rounded hover:bg-green-700">Save</button>
                </div>
            </div>
        </div>
    );
};


const Locations = () => {
    const { activeOrg } = useContext(OrgContext);
    const [locations, setLocations] = useState([]);
    const [newLocation, setNewLocation] = useState({ name: '', contact: '', terminals: 0, members: [] });
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [employeesDialogOpen, setEmployeesDialogOpen] = useState(false);
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 5;
    const token = sessionStorage.getItem('token');

    // Fetch locations on component mount
    useEffect(() => {
        const fetchLocations = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_SRV}/locations/`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        orgid: `${activeOrg._id}`,
                    },
                });
                setLocations(response.data);
            } catch (error) {
                console.error('Error fetching locations:', error);
                toast.error('Failed to fetch locations');
            }
        };
        if (activeOrg) {
            fetchLocations();
        }
    }, [activeOrg, token]);

    // Open the employee dialog for the selected location
    const openEmployeesDialog = (location) => {
        setSelectedLocation(location);
        setEmployeesDialogOpen(true);
    };

    // Close the employee dialog
    const closeEmployeesDialog = () => {
        setEmployeesDialogOpen(false);
    };

    // Open the dialog for adding or editing a location
    const openDialog = (location) => {
        setNewLocation(location ? { ...location } : { name: '', contact: '', terminals: 0, members: [] });
        setIsDialogOpen(true);
    };

    // Close the location dialog
    const closeDialog = () => {
        setIsDialogOpen(false);
    };

    // Update location, including updating members (employees)
    const handleUpdateLocation = async (updatedLocation) => {
        try {
            const method = updatedLocation._id ? 'put' : 'post';
            const endpoint = updatedLocation._id
                ? `${process.env.REACT_APP_SRV}/locations/${updatedLocation._id}`
                : `${process.env.REACT_APP_SRV}/locations`;

            const response = await axios[method](endpoint, updatedLocation, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    orgid: `${activeOrg._id}`,
                },
            });

            if (updatedLocation._id) {
                setLocations(locations.map(location => location._id === response.data._id ? response.data : location));
                toast.success('Location updated successfully');
            } else {
                setLocations([...locations, response.data]);
                toast.success('Location created successfully');
            }

            setIsDialogOpen(false);
            setEmployeesDialogOpen(false);
        } catch (error) {
            console.error(`Error ${updatedLocation._id ? 'updating' : 'creating'} location:`, error);
            toast.error(`Failed to ${updatedLocation._id ? 'update' : 'create'} location`);
        }
    };

    // Delete a location
    const handleDelete = async (id) => {
        try {
            await axios.delete(`${process.env.REACT_APP_SRV}/locations/${id}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    orgid: `${activeOrg ? activeOrg._id : ''}`,
                },
            });

            // Remove the deleted location from the state
            setLocations(locations.filter(location => location._id !== id));
            toast.success('Location deleted successfully');
        } catch (error) {
            console.error('Error deleting location:', error);
            toast.error('Failed to delete location');
        }
    };

    // Search input handler
    const handleSearch = (e) => {
        const query = e.target.value;
        setSearchQuery(query);
    };

    // Pagination handler
    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    // Generate QR codes for terminals in a location
    const generateQRCodes = async (location) => {
        try {
            const { _id: placeId, terminals } = location;
            const baseUrl = `${process.env.REACT_APP_FRONT}/POS`;

            const qrCodes = [];
            for (let i = 1; i <= terminals; i++) {
                const qrUrl = `${baseUrl}?p=${placeId}&t=${i}`;
                const qrCode = await QRCode.toDataURL(qrUrl);
                qrCodes.push(qrCode);
            }

            exportToPDF(qrCodes, location.name);
            toast.success('QR Codes generated successfully');
        } catch (error) {
            console.error("Error generating QR codes:", error);
            toast.error('Failed to generate QR codes');
        }
    };

    // Export QR codes to PDF
    const exportToPDF = (qrCodes, locationName) => {
        const pdf = new jsPDF();
        qrCodes.forEach((qrCode, index) => {
            if (index > 0) {
                pdf.addPage();
            }
            pdf.text(`Location: ${locationName} - terminal# ${index + 1}`, 10, 10);
            pdf.addImage(qrCode, 'PNG', 10, 20, 50, 50);
        });

        pdf.save(`${locationName}_QR_Codes.pdf`);
    };

    // Pagination and search logic
    const filteredLocations = locations.filter(location =>
        location.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredLocations.slice(indexOfFirstItem, indexOfLastItem);

    return (
        <div>
            <ToastContainer /> {/* Toast container for notifications */}
            <div className="mb-4 flex justify-between items-center">
                <input
                    type="text"
                    placeholder="Search locations..."
                    value={searchQuery}
                    onChange={handleSearch}
                    className="px-4 py-2 bg-gray-800 rounded text-white"
                />
                <button onClick={() => openDialog(null)} className="bg-green-600 px-4 py-2 rounded hover:bg-green-700">
                    Add Location
                </button>
            </div>

            <ul>
                {currentItems.map(location => (
                    <li key={location._id} className="bg-gray-800 p-4 mb-2 rounded flex justify-between items-center">
                        <div className="flex items-center">
                            <div>
                                <h3 className="text-xl">{location.name}</h3>
                                <p className="text-gray-400">Contact: {location.contact}</p>
                                <p className="text-gray-400">Terminals: {location.terminals}</p>
                            </div>
                            <BiQr
                                onClick={() => generateQRCodes(location)}
                                className="ml-4 text-5xl text-black hover:text-white cursor-pointer"
                            />
                        </div>

                        <div className="flex">
                            <button
                                onClick={() => openEmployeesDialog(location)}
                                className="bg-yellow-600 px-2 py-1 rounded hover:bg-yellow-700"
                            >
                                Employees
                            </button>
                            <button
                                onClick={() => openDialog(location)}
                                className="bg-blue-600 px-2 py-1 rounded hover:bg-blue-700 ml-2"
                            >
                                Edit
                            </button>
                            <button
                                onClick={() => handleDelete(location._id)}
                                className="bg-red-600 px-2 py-1 rounded hover:bg-red-700 ml-2"
                            >
                                Delete
                            </button>
                        </div>
                    </li>
                ))}
            </ul>

            {/* Pagination */}
            <div className="flex justify-center mt-4">
                {Array.from({ length: Math.ceil(filteredLocations.length / itemsPerPage) }, (_, i) => (
                    <button
                        key={i}
                        onClick={() => handlePageChange(i + 1)}
                        className={`px-3 py-2 mx-1 rounded ${currentPage === i + 1 ? 'bg-golden' : 'bg-gray-800 hover:bg-gray-700'}`}
                    >
                        {i + 1}
                    </button>
                ))}
            </div>

            {/* Add/Edit Location Dialog */}
            {isDialogOpen && (
                <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center">
                    <div className="bg-gray-800 p-6 rounded-lg">
                        <h3 className="text-xl mb-4">{newLocation._id ? 'Edit Location' : 'Add New Location'}</h3>
                        <input
                            type="text"
                            placeholder="Name"
                            value={newLocation.name}
                            onChange={(e) => setNewLocation({ ...newLocation, name: e.target.value })}
                            className="block w-full mb-2 p-2 bg-gray-700 text-white rounded"
                        />
                        <input
                            type="text"
                            placeholder="Contact"
                            value={newLocation.contact}
                            onChange={(e) => setNewLocation({ ...newLocation, contact: e.target.value })}
                            className="block w-full mb-2 p-2 bg-gray-700 text-white rounded"
                        />
                        <label className="text-white mb-2 block">Terminals</label>
                        <input
                            type="number"
                            placeholder="Enter number of terminals"
                            value={newLocation.terminals}
                            onChange={(e) => setNewLocation({ ...newLocation, terminals: e.target.value })}
                            className="block w-full mb-4 p-2 bg-gray-700 text-white rounded"
                        />
                        <button onClick={() => handleUpdateLocation(newLocation)} className="bg-green-600 px-4 py-2 rounded hover:bg-green-700">
                            {newLocation._id ? 'Update Location' : 'Create Location'}
                        </button>
                        <button onClick={closeDialog} className="bg-red-600 px-4 py-2 ml-2 rounded hover:bg-red-700">
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            {/* Employees Dialog */}
            {employeesDialogOpen && (
                <EmployeesDialog
                    location={selectedLocation}
                    onClose={closeEmployeesDialog}
                    handleUpdateLocation={handleUpdateLocation}
                />
            )}
        </div>
    );
};

export default Locations;
